import React, { useState, useEffect } from "react";
import { Box, Tabs, Tab, Button, Typography, Chip } from "@mui/material";
import { CloudUpload, VideoLibrary, Logout, Settings as SettingsIcon, Person } from "@mui/icons-material";
import UploadForm from "./UploadForm";
import VideoList from "./VideoList";
import Settings from "./Settings";

// Componente de panel con pestañas dinámicas según permisos del usuario
const Dashboard = ({ onLogout, currentUser }) => {
  const permissions = currentUser?.permissions || {};
  const isAdmin = currentUser?.role === 'admin';

  // Construir tabs dinámicamente según permisos
  const tabs = [];
  if (isAdmin || permissions.upload_videos) {
    tabs.push({
      id: 'upload',
      label: 'Subir Video',
      icon: <CloudUpload />,
      component: <UploadForm />,
    });
  }
  if (isAdmin || permissions.view_videos) {
    tabs.push({
      id: 'videos',
      label: 'Ver Videos',
      icon: <VideoLibrary />,
      component: <VideoList canDelete={isAdmin || permissions.delete_videos} />,
    });
  }
  if (isAdmin || permissions.settings) {
    tabs.push({
      id: 'settings',
      label: 'Configuración',
      icon: <SettingsIcon />,
      component: <Settings currentUser={currentUser} />,
    });
  }

  const [tabValue, setTabValue] = useState(0);

  // Si el tabValue queda fuera del rango de tabs (ej: por cambio de permisos), resetear
  useEffect(() => {
    if (tabValue >= tabs.length) {
      setTabValue(0);
    }
  }, [tabValue, tabs.length]);

  const handleChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleLogout = () => {
    // Eliminar la autenticación del localStorage
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    onLogout();
  };

  // Si no hay tabs disponibles (usuario sin permisos), mostrar un mensaje
  if (tabs.length === 0) {
    return (
      <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            backgroundColor: "background.paper",
            justifyContent: "flex-end",
            alignItems: "center",
            px: 2,
            py: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Chip
              icon={<Person />}
              label={currentUser?.displayName || currentUser?.username || "Usuario"}
              variant="outlined"
              size="small"
              sx={{ borderColor: "primary.main", color: "text.primary" }}
            />
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Logout />}
              onClick={handleLogout}
              size="small"
            >
              Cerrar Sesión
            </Button>
          </Box>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexGrow: 1, p: 3 }}>
          <Typography variant="h6" color="text.secondary" align="center">
            No tienes permisos asignados. Contacta al administrador para obtener acceso.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {/* Barra de pestañas con info de usuario y botón de logout */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          backgroundColor: "background.paper",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleChange}
          aria-label="Pestañas del sistema"
          textColor="primary"
          indicatorColor="primary"
        >
          {tabs.map((tab, index) => (
            <Tab
              key={tab.id}
              icon={tab.icon}
              label={tab.label}
              id={`tab-${index}`}
              aria-controls={`tabpanel-${index}`}
            />
          ))}
        </Tabs>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Chip
            icon={<Person />}
            label={currentUser?.displayName || currentUser?.username || "Usuario"}
            variant="outlined"
            size="small"
            sx={{ borderColor: "primary.main", color: "text.primary" }}
          />
          {isAdmin && (
            <Chip
              label="Admin"
              color="primary"
              size="small"
              sx={{ fontWeight: "bold" }}
            />
          )}
          <Button
            variant="outlined"
            color="primary"
            startIcon={<Logout />}
            onClick={handleLogout}
            size="small"
          >
            Cerrar Sesión
          </Button>
        </Box>
      </Box>

      {/* Contenido de las pestañas */}
      {tabs.map((tab, index) => (
        <TabPanel key={tab.id} value={tabValue} index={index}>
          {tab.component}
        </TabPanel>
      ))}
    </Box>
  );
};

// Componente auxiliar para mostrar el contenido de cada pestaña
const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
      style={{ height: "calc(100% - 48px)", overflow: "auto" }}
    >
      {value === index && children}
    </div>
  );
};

export default Dashboard;
